"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Kumodi = void 0;
const api_1 = __importDefault(require("./api"));
class Kumodi {
    constructor(config) {
        this.updateContactAttributes = ({ InstanceId, InitialContactId, Attributes, }) => {
            return {
                promise: async () => {
                    try {
                        return await this.api.http({
                            method: 'put',
                            url: `/clients/${InstanceId}/calls/${InitialContactId}/attributes`,
                            data: {
                                attributes: Attributes
                            }
                        });
                    }
                    catch (e) {
                        console.warn(`There was an error posting to kumodi to update attributes for call with ID ${InitialContactId}`);
                        console.warn(e);
                        throw (e);
                    }
                }
            };
        };
        this.getMetricData = (_) => {
            return {
                promise: async () => {
                    throw new Error("getMetricData is not implemented on the kumodi platform currently");
                }
            };
        };
        this.arnRoot = "arn:aws:connect:eu-west-2:475873737205:"; //<-- currently the same for all companies
        this.apiKey = config.apiKey;
        this.clientID = config.clientID;
        this.api = new api_1.default();
        this.api.setToken(config.apiKey);
    }
    describeUserHierarchyStructure(_) {
        //kumodi has no concept of a hierarchy structure so we can just return empty
        return {
            promise: async () => ({ HierarchyStructure: {} })
        };
    }
    listUserHierarchyGroups(_) {
        //kumodi has no concept of a hierarchy structure so we can just return empty
        return {
            promise: async () => {
                return { UserHierarchyGroupSummaryList: [] };
            }
        };
    }
    describeUserHierarchyGroup(_) {
        //kumodi has no concept of a hierarchy structure so we can just return empty
        return {
            promise: async () => {
                return {};
            }
        };
    }
    listContactFlows(_) {
        // kumodi has no concept of contact flows so just an empty response will do.
        return {
            promise: async () => {
                return { ContactFlowSummaryList: [] };
            }
        };
    }
    listSecurityProfiles({ InstanceId }) {
        return {
            promise: async () => {
                return {
                    SecurityProfileSummaryList: [
                        {
                            Id: `${this.arnRoot}instance/${InstanceId}/security-profile/agent`,
                            Arn: `${this.arnRoot}instance/${InstanceId}/security-profile/agent`,
                            Name: "Agent"
                        },
                        {
                            Id: `${this.arnRoot}instance/${InstanceId}/security-profile/admin`,
                            Arn: `${this.arnRoot}instance/${InstanceId}/security-profile/admin`,
                            Name: "Admin"
                        },
                    ]
                };
            }
        };
    }
    listRoutingProfiles({ InstanceId }) {
        return {
            promise: async () => {
                return {
                    RoutingProfileSummaryList: [
                        {
                            Id: `${this.arnRoot}instance/${InstanceId}/routing-profile/default`,
                            Arn: `${this.arnRoot}instance/${InstanceId}/routing-profile/default`,
                            Name: "default"
                        }
                    ]
                };
            }
        };
    }
    listQueues({ InstanceId, QueueTypes, NextToken }) {
        return {
            promise: async () => {
                const { teams, allTeamMembers } = await this._getTeamsAndTeamMembers(InstanceId);
                let teamQueues = [];
                if (QueueTypes.includes("STANDARD")) {
                    teamQueues = teams.map((team) => ({
                        Id: `${this.arnRoot}instance/${InstanceId}/queue/${team.ID}`,
                        Arn: `${this.arnRoot}instance/${InstanceId}/queue/${team.ID}`,
                        Name: team.name,
                        QueueType: "STANDARD"
                    }));
                }
                let userQueues = [];
                if (QueueTypes.includes("AGENT")) {
                    //Each user has their own pseudoqueue, for now we're returning those too.
                    userQueues = allTeamMembers.map((member) => ({
                        Id: `${this.arnRoot}instance/${InstanceId}/queue/${member.userID}`,
                        Arn: `${this.arnRoot}instance/${InstanceId}/queue/${member.userID}`,
                        Name: `My Calls (${member.userFullName})`,
                        QueueType: "AGENT"
                    }));
                }
                return {
                    QueueSummaryList: [...teamQueues, ...userQueues]
                };
            }
        };
    }
    getCurrentMetricData({ InstanceId, Filters, CurrentMetrics, Groupings }) {
        return {
            promise: async () => {
                const emptyResonse = {};
                const summariseAllVoiceCallsIntoOne = !Groupings || (Groupings.length == 1 && Groupings[0] === "CHANNEL");
                let { allTeamMembers, teams } = await this._getTeamsAndTeamMembers(InstanceId, Filters === null || Filters === void 0 ? void 0 : Filters.Queues);
                if (!teams.length)
                    return emptyResonse;
                if (Filters === null || Filters === void 0 ? void 0 : Filters.Queues) {
                    teams = teams.filter((team) => (Filters.Queues.find((arn) => arn === `${this.arnRoot}instance/${InstanceId}/queue/${team.ID}`)));
                }
                const presenceInfo = await this._getPresenceInfoForTeamMembers(allTeamMembers, InstanceId);
                const queueInfo = await this._getQueueInfo(InstanceId);
                const teamsWithStats = this._attachStatsToTeams(teams, presenceInfo, queueInfo, InstanceId);
                let sortedTeamsWithStats = teamsWithStats;
                if (Filters === null || Filters === void 0 ? void 0 : Filters.Queues) { //Need to preserve order like connect does so re-order teams to match the order of the ID list we were passed as a filter.
                    sortedTeamsWithStats = Filters.Queues.map((queueARN) => (teamsWithStats.find((team) => team.arn === queueARN)));
                }
                if (summariseAllVoiceCallsIntoOne) {
                    // fold all the data together into a summary
                    let allQueuesIntoOneQueue = sortedTeamsWithStats.reduce((current, next) => ({
                        activeUsers: 0,
                        inactiveUsers: 0,
                        queueLength: current.queueLength + next.queueLength,
                        totalWaitTime: current.totalWaitTime + next.totalWaitTime,
                        maxWaitTime: Math.max(current.totalWaitTime, next.totalWaitTime),
                        minWaitTime: Math.min(current.minWaitTime, next.minWaitTime),
                        averageWaitTime: current.averageWaitTime,
                        //We don't use the following so just hardcode anything.
                        arn: "",
                        ID: "",
                        name: "",
                        enabled: true,
                        callRoute: { teamMembers: [] },
                        serverSideQueue: true
                    }));
                    allQueuesIntoOneQueue.activeUsers = presenceInfo.filter((user) => user.presence === "active").length;
                    allQueuesIntoOneQueue.inactiveUsers = presenceInfo.filter((user) => user.presence === "inactive").length;
                    let queueStats = [{
                            "Collections": CurrentMetrics.map(Metric => ({
                                Metric,
                                Value: this._getMetricValue(Metric.Name, allQueuesIntoOneQueue)
                            })),
                            "Dimensions": {
                                Channel: "VOICE",
                            }
                        }];
                    return {
                        "DataSnapshotTime": new Date(),
                        "MetricResults": queueStats
                    };
                }
                else {
                    let queueStats = sortedTeamsWithStats.map(team => ({
                        "Collections": CurrentMetrics.map(Metric => ({
                            Metric,
                            Value: this._getMetricValue(Metric.Name, team)
                        })),
                        "Dimensions": {
                            Channel: "VOICE",
                            Queue: {
                                Arn: team.arn,
                                Id: team.arn
                            }
                        }
                    }));
                    return {
                        "DataSnapshotTime": new Date(),
                        "MetricResults": queueStats
                    };
                }
            }
        };
    }
    _lastIDFromARN(arn) {
        return arn.split('/').slice(-1)[0];
    }
    async _getTeamsAndTeamMembers(InstanceId, QueueARNs) {
        const teams = (await this.api.http({
            method: 'get',
            url: `/clients/${InstanceId}/teams`
        })).data.filter((team) => (team.serverSideQueue)); //Filter out teams where queues aren't active.
        if (QueueARNs) {
            let ids = QueueARNs.map((arn) => this._lastIDFromARN(arn));
            teams.filter((team) => ids.includes(team.ID));
        }
        const onlyUnique = (teamMember, index, array) => (index == array.findIndex((thisMember) => (thisMember.userID === teamMember.userID)));
        let allTeamMembers = teams.flatMap(team => (team.callRoute.teamMembers)).filter(onlyUnique);
        return { allTeamMembers, teams };
    }
    async _getQueueInfo(InstanceId) {
        const queueInfo = (await this.api.http({
            method: 'get',
            url: `/clients/${InstanceId}/queues`
        })).data;
        return queueInfo;
    }
    async _getPresenceInfoForTeamMembers(teamMembers, InstanceId) {
        //Currently the endpoint only supports getting this as a user, so we make the request for 'randomUser'
        let randomUser = teamMembers[0];
        const presenceInfo = (await this.api.http({
            method: 'get',
            url: `/clients/${InstanceId}/users/${randomUser.userID}/transfers`
        })).users;
        if (teamMembers.length > 1) { //since presence only shows 'other' users, we need to hit it again as a different user to get randomUser[0]'s presence
            let anotherRandomUser = teamMembers[1];
            const presenceInfo2 = (await this.api.http({
                method: 'get',
                url: `/clients/${InstanceId}/users/${anotherRandomUser.userID}/transfers`
            })).users;
            let randomUserPresenceInfo = presenceInfo2.find((user) => user.ID === randomUser.userID);
            randomUserPresenceInfo && presenceInfo.push(randomUserPresenceInfo);
        }
        return presenceInfo;
    }
    _attachStatsToTeams(teams, presenceInfo, queueInfo, InstanceId) {
        return teams.map((team) => {
            var _a;
            const thisTeamsQueueStats = ((_a = queueInfo.find((queue) => queue.ID === team.ID)) === null || _a === void 0 ? void 0 : _a.metaData) || { queueLength: 0, totalWaitTime: 0, maxWaitTime: 0, minWaitTime: 0, averageWaitTime: 0 };
            const thisTeamsUsers = team.callRoute.teamMembers;
            const userPresenceForThisTeamsUsers = presenceInfo.filter((presenceInfo) => thisTeamsUsers.find((teamMember) => teamMember.userID == presenceInfo.ID));
            const activeUsers = userPresenceForThisTeamsUsers.filter((presence) => presence.presence === "active").length;
            const inactiveUsers = userPresenceForThisTeamsUsers.filter((presence) => presence.presence === "inactive").length;
            return Object.assign(Object.assign(Object.assign(Object.assign({}, team), { activeUsers,
                inactiveUsers }), thisTeamsQueueStats), { arn: `${this.arnRoot}instance/${InstanceId}/queue/${team.ID}` });
        });
    }
    _getMetricValue(metricName, team) {
        if (metricName === "AGENTS_ONLINE" || metricName == "AGENTS_STAFFED")
            return team.inactiveUsers + team.activeUsers;
        if (metricName === "AGENTS_AVAILABLE")
            return team.activeUsers;
        if (metricName === "AGENTS_NON_PRODUCTIVE")
            return team.inactiveUsers;
        if (metricName === "CONTACTS_IN_QUEUE")
            return team.queueLength;
        if (metricName === "OLDEST_CONTACT_AGE")
            return team.maxWaitTime;
        return 0;
    }
}
exports.Kumodi = Kumodi;
class KumodiAPI {
}
